import { PropsWithChildren, useMemo } from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import {
  DashboardLayout,
  DashboardNavMenuItem,
  useOidc,
  useScope,
  useScopeIds,
} from '@nohara-dxdev/react-oidc';
import { createOrgScope, OrgEntity, permission } from '@nohara-dxdev/oidc-core';
import { useUserOrgs } from '@nohara-dxdev/react-oidc';
import { LoadingScreen } from '@nohara-dxdev/react-core';

import NotFoundRoute from '@/app/routes/not-found';
import { AppAssignmentCheck } from '@/features/apps/components/app-assignment-check';
import { paths } from '@/config/paths';

const readRolePermissions = [permission.role.read];
const readUserPermissions = [permission.user.read];
const updateOrgPermissions = [permission.org.update];

export const AppLayout = ({ children }: PropsWithChildren) => {
  const { userId } = useOidc();
  const { t } = useTranslation();
  const { orgId } = useScopeIds();
  const { data: orgs } = useUserOrgs(userId);
  const { entities } = useScope();
  const org = orgId ? (entities.get(orgId) as OrgEntity) : undefined;

  const hasMoreThanOneOrg = !!orgs?.length && orgs.length > 1;
  const orgScope = createOrgScope(orgId);

  const sections = useMemo((): DashboardNavMenuItem[][] => {
    const firstSection = [];
    if (hasMoreThanOneOrg) {
      firstSection.push({
        path: paths.home.path,
        icon: <SwitchAccountIcon />,
        label: t('navigation.menu.switchOrg'),
      });
    }

    const secondSection = orgId
      ? [
          {
            path: paths.org.dashboard.getHref(orgId),
            icon: <DashboardIcon />,
            label: t('navigation.menu.dashboard'),
          },
          {
            path: paths.org.roles.getHref(orgId),
            icon: <BadgeIcon />,
            label: t('navigation.menu.roles'),
            requireAll: readRolePermissions,
            scope: orgScope,
          },
          {
            path: paths.org.users.getHref(orgId),
            icon: <PeopleIcon />,
            label: t('navigation.menu.users'),
            requireAll: readUserPermissions,
            scope: orgScope,
          },
          {
            path: paths.org.orgProfile.getHref(orgId),
            icon: <SettingsIcon />,
            label: t('navigation.menu.orgProfile'),
            requireAll: updateOrgPermissions,
            scope: orgScope,
          },
        ]
      : [];

    const sections: DashboardNavMenuItem[][] = [];

    if (firstSection.length > 0) {
      sections.push(firstSection);
    }

    if (secondSection.length > 0) {
      sections.push(secondSection);
    }

    return sections;
  }, [hasMoreThanOneOrg, orgId, orgScope, t]);

  const navSubTitle = orgId ? (
    <Box display='flex' alignItems='center'>
      <Chip label={`ID: ${orgId.substring(0, 24)}`} size='small' sx={{ ml: -2 }} />
      <IconButton
        size='small'
        onClick={() => {
          navigator.clipboard.writeText(orgId);
        }}
        title={t('common.copyToClipboard')}
      >
        <ContentCopyIcon fontSize='small' />
      </IconButton>
    </Box>
  ) : undefined;

  return (
    <AppAssignmentCheck loadingElement={<LoadingScreen />} fallbackElement={<NotFoundRoute />}>
      <DashboardLayout
        sections={sections}
        navTitle={org?.name || t('navigation.title')}
        navSubTitle={navSubTitle}
        pageTitle={org?.name || t('navigation.appTitle')}
        paths={{
          logout: paths.auth.login.path,
          userProfile: paths.org.userProfile.getHref(),
        }}
      >
        {children}
      </DashboardLayout>
    </AppAssignmentCheck>
  );
};
